import _ from 'lodash'
import { tmrOperationController } from 'tmr/controllers/operation/operation'
import './encoding.scss'
import templateUrl from './encoding.html'
import icon from './encoding.svg'
import readerImg from 'tmrAssets/images/rfid-reader.gif'

/**
 * Encoding Operation route.
 *
 * @namespace routes.OperationEncoding
 * @type {TmrRoute}
 */
export const route = {
  name: 'operations.encoding',
  label: 'OPERATIONS.ENCODING.TITLE',
  url: '/encoding',
  resolve: {
    antenna: /*@ngInject*/ ActiveWorkstation => ActiveWorkstation.getAntennas(),
    /** Retrieves the stock zones of the place of the active user. */
    zones: /*@ngInject*/ (Zone, loggedUser) =>
      Zone.query({
        'place.id': loggedUser.place.id,
        zoneType: 'STOCK',
        orderBy: 'DESCRIPTION',
        asc: true,
      }).$promise,
  },
  component: 'tmrOperationEncoding',
  accessControl: ['Operation.perform', 'Operation.Encoding.perform'],
  weight: 0,
  icon,
}

/**
 * Encoding Operation component
 *
 * @type {ngComponent}
 * @todo document
 */
export const tmrOperationEncoding = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrOperationEncodingController',
  bindings: {
    antenna: '<',
    zones: '<',
    zone: '<',
  },
}

/**
 * Controller of component {@link tmrOperationEncoding}.
 *
 * @type {ngController}
 */
export class tmrOperationEncodingController extends tmrOperationController {
  constructor(
    $mdDialog,
    TmrToast,
    TmrMovementHelper,
    TmrOperationHelper,
    $rootElement,
    $rootScope,
    $state,
    $timeout,
    tmrEncodingEpcReadModes,
    TmrEpc,
    TmrLocalStorage,
    MovementEncoding,
    Item,
    Product
  ) {
    'ngInject'
    super($mdDialog, TmrToast, TmrMovementHelper, TmrOperationHelper)
    Object.assign(this, {
      $rootElement,
      $rootScope,
      $state,
      $timeout,
      tmrEncodingEpcReadModes,
      TmrEpc,
      TmrLocalStorage,
      MovementEncoding,
      Item,
      Product,
    })
  }

  $onInit() {
    super.$onInit()
    this.readerImg = readerImg
    this.epcReadMode = this.getConfig('encodingEpcReadMode')
    this.zone =
      this.zones.find(
        ({ id }) => id == this.getConfig('encodingDefaultZone')
      ) ?? this.zones[0]
  }

  $onDestroy() {
    super.$onDestroy()
    this.$rootElement.removeClass('tmr-freeze-content')
    ;(this.deregisterOnEpcRead || _.noop)()
    this.$timeout.cancel(this.epcTimeout)
  }

  initVars() {
    super.initVars()
    this.productBarcode = null
    this.product = null
    this.readerEpc = null
    this.settingsChanged = false
  }

  onProductBarcodeChange(event) {
    if (event.key == 'Enter' && this.productBarcode) {
      this.loadingProduct = true
      this.Product.getByUpc(this.productBarcode)
        .$promise.then(product => {
          if (product && product.id) {
            this.product = product
            if (this.epcReadMode == 'barcode')
              return this.initEpcReadFromBarcode()
            else return this.initEpcReadFromReader()
          }
          throw new Error(`No product with Upc "${this.productBarcode}"`)
        })
        .catch(err => this.handleError(err))
        .finally(() => (this.loadingProduct = false))
    }
  }

  /**
   * Initialize tag reading when epcReadMode is "barcode"
   */
  initEpcReadFromBarcode() {
    this.waitingBarcodeEpc = true
  }

  /**
   * Initialize tag reading when epcReadMode is "reader"
   */
  initEpcReadFromReader() {
    this.deregisterOnEpcRead = this.$rootScope.$on(
      'epcread',
      (event, epc, antenna) => this.onEpcRead(epc, antenna)
    )
    this.antenna.$start().catch(err => this.TmrToast.exception(err))
  }

  /**
   * Antenna read an epc event.
   */
  onEpcRead(epc) {
    // If we already have an epc, throw an error.
    if (this.readerEpc) {
      this.deregisterOnEpcRead()
      this.$timeout.cancel(this.epcTimeout)
      return this.antenna
        .$stop()
        .then(() =>
          this.TmrToast.exception(
            new Error(
              `Multiple tags read. Remove all products except one try again.`
            ),
            false
          )
        )
        .catch(err => this.TmrToast.exception(err))
        .then(() => this.onClear())
    }
    this.readerEpc = epc
    this.epcTimeout = this.$timeout(1000)
    return this.epcTimeout
      .then(() => this.antenna.$stop())
      .then(() => this.processEpc(this.readerEpc))
  }

  /**
   * Event fired when barcode on the tag was read. Used when epcReadMode is "barcode".
   */
  onTagBarcodeChange(event) {
    if (event.key == 'Enter' && this.barcodeEpc) {
      this.waitingBarcodeEpc = false
      return this.processEpc(this.padBarcodeEpc())
    }
  }

  /**
   * Fills epc from a barcode with 0s up to 24 characters
   */
  padBarcodeEpc() {
    return _.padStart(this.barcodeEpc, 24, '0')
  }

  /**
   * Validates, then writes an epc, handling errors and state.
   */
  processEpc(epc) {
    this.encoding = true
    return this.validateEpc(epc)
      .then(epc => this.write(epc))
      .catch(err => this.handleError(err))
      .finally(() => (this.encoding = false))
  }

  /**
   * Verifico che non ci sia già un item a sistema con questo epc
   */
  validateEpc(epc) {
    return this.Item.query({ epc }).$promise.then(res => {
      if (res.length) throw new Error(`An item with Epc ${epc} already exists`)
      return epc
    })
  }

  write(targetEpc) {
    let serial

    // Prendo il seriale.
    this.TmrEpc.getNextSerial(this.product)
      .then(res => (serial = res))
      // Genero l'epc da scrivere nel tag.
      .then(() => this.TmrEpc.encode(this.product.code, serial, 6))
      // Prova a scrivere il tag.
      .then(epc => this.tryWriteTag(serial, targetEpc, epc))
      .catch(err => this.handleError(err))
      .finally(() => (this.encoding = false))
  }

  encodingSuccess() {
    this.TmrToast.translate('OPERATIONS.ENCODING.ENCODINGSUCCEDED')
    this.$state.reload()
  }

  handleError(err) {
    this.TmrToast.exception(err)
    this.$state.reload()
  }

  tryWriteTag(serial, oldEpc, newEpc) {
    // Prova a scrivere 2 volte.
    return (
      this.writeTag(oldEpc, newEpc)
        .catch(() => this.writeTag(oldEpc, newEpc))
        // Leggi il tag. Riprova una volta e sennò fallisci.
        .then(() => this.read(newEpc).catch(() => this.read(newEpc)))
        // Se tutto va bene, crea item tramite un movement di tipo encoding.
        .then(
          () =>
            this.MovementEncoding.save({
              encodingZone: { id: this.zone.id },
              items: [
                {
                  epc: newEpc,
                  upc: this.product.code,
                  serial,
                  product: { id: this.product.id },
                },
              ],
            }).$promise
        )
        .then(() => this.encodingSuccess())
    )
  }

  writeTag(targetEpc, data) {
    return this.antenna.$write('epc', { targetEpc, data })
  }

  read(targetEpc) {
    return this.antenna.$read('epc', { targetEpc })
  }

  settingsToggle() {
    this.showSettings = !this.showSettings
    this.$rootElement.toggleClass('tmr-freeze-content', this.showSettings)
    // Reload state with updated settings.
    if (this.settingsChanged) this.$state.reload()
  }

  onZoneChange() {
    this.settingsChanged = true
    this.setConfig('encodingDefaultZone', this.zone.id)
  }

  onEpcReadModeChange() {
    this.settingsChanged = true
    this.setConfig('encodingEpcReadMode', this.epcReadMode)
  }
}
